import moment from 'moment'
import queryString, { ParsedQuery } from 'query-string'
import innerText from 'react-innertext'

import { heicTo, isHeic } from 'heic-to'

// import { ClientJS } from 'clientjs'

import styleToInlineStyle from 'libs/style-to-inline'

import i18n from 'i18n'
// const client = new ClientJS()

/**
 * File/Blob을 base64 문자열로 변환한다 (dataURL prefix 제거).
 * 큰 파일에서도 메인 스레드 블록 없이 동작하도록 native FileReader를 사용한다.
 *
 * 실패 케이스 (모두 reject):
 *   - file이 null/undefined
 *   - readAsDataURL 호출 자체가 throw (보안 정책 등)
 *   - read 도중 onerror (DOMException → reader.error)
 *   - read 도중 onabort (외부에서 reader.abort() 호출)
 *   - reader.result가 string이 아닌 경우 (예상치 못한 브라우저 동작)
 *   - dataURL 형식 위반 (',' 미존재)
 */
export function fileToBase64(file: File | Blob | null | undefined): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file == null) {
      reject(new Error('fileToBase64: file is null'))
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('fileToBase64: unexpected reader result type'))
        return
      }
      // base64 alphabet에 ','가 없으므로 첫 콤마 split이 안전.
      const commaIdx = result.indexOf(',')
      if (commaIdx < 0) {
        reject(new Error('fileToBase64: invalid dataURL format'))
        return
      }
      resolve(result.substring(commaIdx + 1))
    }

    reader.onerror = () => {
      reject(reader.error ?? new Error('fileToBase64: read failed'))
    }

    reader.onabort = () => {
      reject(new Error('fileToBase64: read aborted'))
    }

    try {
      reader.readAsDataURL(file)
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)))
    }
  })
}

/**
 * PR-N9a-1 — File/Blob 을 data URL 전체 문자열 (`data:<mime>;base64,...`) 로 변환.
 *
 * `fileToBase64` 는 prefix 제거 후 base64 만 반환하므로 `<img src="data:...">` 등의 용도엔
 * 그대로 쓸 수 없다. 본 함수는 prefix 포함된 원본 dataURL 을 반환한다.
 */
export function blobToDataUrl(blob: Blob | null | undefined): Promise<string> {
  return new Promise((resolve, reject) => {
    if (blob == null) {
      reject(new Error('blobToDataUrl: blob is null'))
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('blobToDataUrl: unexpected reader result type'))
        return
      }
      resolve(result)
    }

    reader.onerror = () => {
      reject(reader.error ?? new Error('blobToDataUrl: read failed'))
    }

    reader.onabort = () => {
      reject(new Error('blobToDataUrl: read aborted'))
    }

    try {
      reader.readAsDataURL(blob)
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)))
    }
  })
}

export function getISODateStr(timeStr: string | null | undefined): string {
  if (timeStr == null || timeStr.length === 0) {
    return '-'
  }

  let utcTime = timeStr
  if (
    timeStr.length != null &&
    timeStr.indexOf('T') > 0 &&
    timeStr.indexOf('Z') === -1 &&
    timeStr.indexOf('+') === -1
  ) {
    utcTime = `${timeStr}Z`
  }
  return utcTime
}

export interface TimeFormatInput {
  dateFormat?: string | null
  timeFormat?: string | null
  dateTimeFormat?: string | null
}

export interface ResolvedTimeFormats {
  dateFormat: string
  timeFormat: string
  dateTimeFormat: string
}

// 시간 형식은 따로 처리함. (settings의 값에서 계산이 필요한 부분이 존재함.)
export function resolveTimeFormats(input: TimeFormatInput): ResolvedTimeFormats {
  const { dateFormat: dFormat, timeFormat: tFormat, dateTimeFormat: dtFormat } = input

  const timeFormat = tFormat || 'LT'
  const dateFormat = dFormat || 'L'

  let dateTimeFormat = dtFormat
  if (dateTimeFormat == null) {
    // 명시적으로 선언되지 않은 경우. 임의의 값으로 계산됨.
    if (tFormat == null && dFormat == null) {
      dateTimeFormat = 'LLLL'
    } else {
      switch (i18n.language) {
        case 'ko-KR':
          dateTimeFormat = `${dateFormat} (ddd) ${timeFormat}`
          break
        case 'ja-JP':
          dateTimeFormat = `${dateFormat} (ddd) ${timeFormat}`
          break
        case 'en-US':
        default:
          dateTimeFormat = `ddd, ${dateFormat} ${timeFormat}`
          break
      }
    }
  }

  return { dateFormat, timeFormat, dateTimeFormat }
}

export function timeFormat(
  time: string | Date | number | null | undefined,
  formatStr: string,
): string {
  if (time == null || (typeof time === 'string' && time.length === 0)) {
    return '-'
  }

  let utcTime: string
  if (time instanceof Date) {
    utcTime = time.toISOString()
  } else if (typeof time === 'number') {
    utcTime = new Date(time).toISOString()
  } else {
    utcTime = time
    if (
      time.length != null &&
      // time.indexOf('T') > 0 &&
      time.indexOf('Z') === -1 &&
      time.indexOf('+') === -1
    ) {
      utcTime = `${time}Z`
    }
  }
  return moment(utcTime).format(formatStr)
}

export function timeFormatDefault(time: string | null | undefined, defaultStr?: string): string {
  if (time == null || time.length === 0) {
    return defaultStr || '-'
  }

  let utcTime = time
  if (
    time.length != null &&
    // time.indexOf('T') > 0 &&
    time.indexOf('Z') === -1 &&
    time.indexOf('+') === -1
  ) {
    utcTime = `${time}Z`
  }

  return `${timeFormat(utcTime, 'l')} ${timeFormat(time, 'LT')}`
}

export function timeFormatUTC(time: string | null | undefined, formatStr: string): string {
  if (time == null || time.length === 0) {
    return '-'
  }

  let utcTime = time
  if (
    time.length != null &&
    // time.indexOf('T') > 0 &&
    time.indexOf('Z') === -1 &&
    time.indexOf('+') === -1
  ) {
    utcTime = `${time}Z`
  }
  return moment(utcTime).utc().format(formatStr)
}

export function timeFormatUTCDefault(time: string | null | undefined, defaultStr?: string): string {
  if (time == null || time.length === 0) {
    return defaultStr || '-'
  }

  let utcTime = time
  // if (time.length != null && time.indexOf('T') > 0 && time.indexOf('Z') === -1) {
  if (time.length != null && time.indexOf('Z') === -1) {
    utcTime = `${time}Z`
  }

  return `${timeFormatUTC(utcTime, 'l')} ${timeFormatUTC(time, 'LT')}`
}

interface EmailAddress {
  user: string
  host: string
  address: string
}

export function emailAddress(emailStr: string | null | undefined): EmailAddress | null {
  if (emailStr) {
    const pos = emailStr.indexOf('@')
    if (pos >= 0) {
      return {
        user: emailStr.substring(0, pos),
        host: emailStr.substring(pos + 1),
        address: emailStr,
      }
    }
  }
  return null
}

export function localeDateTime(num: number | null | undefined): string {
  if (num != null) {
    const time = new Date(num)
    return time.toLocaleString()
  }
  return ''
}

export function compareStr(a: string | null | undefined, b: string | null | undefined): number {
  // if (a > b) {
  //   return 1
  // }
  // if (a < b) {
  //   return -1
  // }
  // return 0
  const aStr = a || ''
  const bStr = b || ''
  return aStr.localeCompare(bStr)
}

export function getSearchParams(): ParsedQuery<string | number> {
  const parsed = queryString.parse(window.location.search, { parseNumbers: true })
  return parsed
}

export function makeSearchString(params: Record<string, any> | null | undefined): string {
  if (params != null) {
    const search = queryString.stringify(params)
    return search
  }
  return ''
}

let paramProps: ParsedQuery<string | number> = {}
const paramChangeListeners: Array<(params: ParsedQuery<string | number>) => void> = []

export function parseSearchParam(): void {
  paramProps = getSearchParams()
}

export function encodeFilterMap(
  filterMap: Record<string, any[]>,
  vars: { filters: Record<string, any> },
  filters: Record<string, any[]>,
): string {
  let filterStr = ''
  Object.entries(filterMap).forEach(([key, value]) => {
    const pos = key.lastIndexOf('.')
    const filterKey = key.substring(pos + 1)
    setInFilter(vars.filters, filterKey, filters[key])
    if (filters[key] != null) {
      filterStr += `${key}:`
      let isFirst = true
      filters[key].forEach((val) => {
        const idx = value.indexOf(val)
        if (isFirst !== true) {
          filterStr += `,`
        }
        filterStr += `${idx}`
        isFirst = false
      })
    }
    filterStr += `;`
  })

  return filterStr
}

export function decodeFilterMap(nfilterStr: string): Record<string, string[]> {
  const nfilter: Record<string, string[]> = {}
  const entries = nfilterStr.split(';')
  entries.forEach((str) => {
    if (str.length > 0) {
      const entry = str.split(':')
      if (entry.length > 1 && entry[1].length > 0) {
        const idxList = entry[1].split(',')
        const arr: string[] = []
        idxList.forEach((idx) => {
          arr.push(idx)
        })
        nfilter[entry[0]] = arr
      }
    }
  })
  return nfilter
}

export function encodeSortMap(sortMap: Record<string, boolean>): string {
  let sortStr = ''
  Object.entries(sortMap).forEach(([key, value]) => {
    sortStr += `${key}:${value ? '1' : '0'};`
  })

  return sortStr
}

export function decodeSortMap(nsortStr: string): Record<string, boolean> {
  const nsort: Record<string, boolean> = {}
  const entries = nsortStr.split(';')
  entries.forEach((str) => {
    if (str.length > 0) {
      const entry = str.split(':')
      if (entry.length > 1 && entry[1].length > 0) {
        nsort[entry[0]] = entry[1] === '1'
      }
    }
  })

  return nsort
}

interface SearchFilterValue {
  pKey: string
  filter?: string
  value: string | number
}

export function encodeNSearch(searchFilters: Record<string, string | SearchFilterValue>): string {
  // return JSON.stringify(searchFilters);

  // value, value.value는 null이 될 수 없다. null 인 경우는 itemKey를 통해 전달됨.
  let searchStr = ''
  Object.entries(searchFilters).forEach(([key, value]) => {
    if (typeof value === 'string') {
      searchStr += `${key.length}I0I0I${value.length}I${key}III${value}I`
    } else {
      const val = typeof value.value === 'string' ? value.value : String(value.value)
      const filter = value.filter != null ? value.filter : ''
      searchStr += `${key.length}I${value.pKey.length}I${filter.length}I${val.length}I${key}I${value.pKey}I${filter}I${val}I`
    }
  })

  return searchStr
}

export function decodeNSearch(nsearchStr: string): Record<string, string | SearchFilterValue> {
  // return JSON.parse(nsearchStr)

  const nsearch: Record<string, string | SearchFilterValue> = {}
  let pos = 0
  let ppos = 0
  const len: number[] = []
  const str: string[] = []
  let i: number
  let tmp: string

  while (nsearchStr.indexOf('I', ppos) > -1) {
    for (i = 0; i < 4; i += 1) {
      pos = nsearchStr.indexOf('I', ppos)
      if (pos < 0) {
        throw new Error('nsearch param error1.')
      }
      tmp = nsearchStr.substring(ppos, pos)
      ppos = pos + 1
      len[i] = parseInt(tmp, 10)
    }

    for (i = 0; i < 4; i += 1) {
      pos = ppos + len[i]
      if (pos > nsearchStr.length) {
        throw new Error('nsearch param error2.')
      }
      str[i] = nsearchStr.substring(ppos, pos)
      ppos = pos + 1
    }

    const [key, pKey, filter, value] = str
    if (pKey.length === 0) {
      nsearch[key] = value
    } else {
      nsearch[key] = {
        pKey,
        filter,
        value,
      }
    }
  }

  return nsearch
}

export function processNParam(): void {
  const sparams: any = paramProps

  if (sparams.nfilter != null) {
    const nfilterStr = decodeURIComponent(sparams.nfilter as string)

    const nfilter = decodeFilterMap(nfilterStr)

    if (Object.keys(nfilter).length > 0) {
      sparams.nfilter = nfilter
    } else {
      delete sparams.nfilter
    }
  }

  if (sparams.nsort != null) {
    const nsortStr = decodeURIComponent(sparams.nsort as string)

    const nsort = decodeSortMap(nsortStr)

    if (Object.keys(nsort).length > 0) {
      sparams.nsort = nsort
    } else {
      delete sparams.nsort
    }
  }

  if (sparams.nsearch != null) {
    let nsearch = decodeURIComponent(sparams.nsearch as string)
    const nsearchResult = decodeNSearch(nsearch)

    if (Object.keys(nsearchResult).length > 0) {
      sparams.nsearch = nsearchResult
    } else {
      delete sparams.nsearch
    }
  }

  paramChangeListeners.forEach((func) => {
    func(paramProps)
  })
}

export function getSearchParamProps(): ParsedQuery<string | number> {
  return paramProps
}

export function addParamChangeListener(func: (params: ParsedQuery<string | number>) => void): void {
  if (paramChangeListeners.indexOf(func) < 0) {
    paramChangeListeners.push(func)
  }
}

export function removeParamChangeListener(
  func: (params: ParsedQuery<string | number>) => void,
): void {
  const pos = paramChangeListeners.indexOf(func)
  if (pos >= 0) {
    paramChangeListeners.splice(pos, 1)
  }
}

interface Location {
  pathname: string
  search: string
  state?: { replaceUrl?: boolean }
}

export function checkPageReplace(location: Location, prevLocation: Location): boolean {
  if (
    location.pathname === prevLocation.pathname &&
    location.search !== prevLocation.search &&
    (location.state == null || location.state.replaceUrl !== true)
  ) {
    return false
  }
  return true
}

export function getTotalPageCnt(total: number, pageSize: number): number {
  let totalPage = Math.floor(total / pageSize)

  if (total > pageSize * totalPage) {
    totalPage += 1
  }

  return totalPage
}

// babel을 통해 locale 키를 뽑아내기 위해 사용.
export function i18Key(key: string): string {
  return key
}

interface PresetItem {
  key: string
  value: any
}

export function setFilter(
  filters: Record<string, any>,
  key: string,
  value: string | SearchFilterValue | null | undefined,
  isRemove?: boolean,
  preset?: Record<string, PresetItem[]>,
): void {
  if (value != null && isRemove !== true) {
    if (typeof value === 'string') {
      filters[`${key}Contains`] = value
    } else {
      let val = value.value
      if (preset != null && preset[key] != null) {
        const pItem = preset[key].find((item) => item.key === value.pKey)
        if (pItem) {
          val = pItem.value
        }
      }
      if (value.filter != null) {
        filters[value.filter] = val
      } else {
        filters[`${key}Contains`] = val
      }
    }
  } else if (value != null && (value as SearchFilterValue).filter != null) {
    delete filters[(value as SearchFilterValue).filter!]
  } else {
    delete filters[`${key}Contains`]
  }
}

export function setInFilter(
  filters: Record<string, any>,
  key: string,
  value: any[] | null | undefined,
): void {
  if (value != null && value.length > 0) {
    filters[`${key}In`] = value
  } else {
    delete filters[`${key}In`]
  }
}

export function getTimeStr(t: number): string {
  if (t <= 0) {
    return '00:00'
  }
  const minute = Math.floor(t / 60)
  const second = Math.floor(t % 60)
  const minStr = minute > 9 ? minute : `0${minute}`
  const secStr = second > 9 ? second : `0${second}`
  const str = `${minStr}:${secStr}`
  return str
}

export function parseJwt(token: string): any {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join(''),
  )

  return JSON.parse(jsonPayload)
}

export const decodeJwtPayload = (token: string | null | undefined): any => {
  if (!token) return null

  try {
    const payloadBase64 = token.split('.')[1]
    // Add padding if needed
    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/')
    const padding = '='.repeat((4 - (base64.length % 4)) % 4)
    const base64WithPadding = base64 + padding

    const decodedStr = atob(base64WithPadding)
    return JSON.parse(decodedStr)
  } catch (error) {
    console.error('Error decoding JWT payload:', error)
    return null
  }
}

// Helper function from: http://stackoverflow.com/a/7557433/274826
export function isElementInViewport(el: Element): boolean {
  // special bonus for those using jQuery
  // if (typeof jQuery === "function" && el instanceof jQuery) {
  //   el = el[0];
  // }
  const rect = el.getBoundingClientRect()
  return (
    (rect.top <= 0 && rect.bottom >= 0) ||
    (rect.bottom >= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight)) ||
    (rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight))
  )
}

interface AnimationObj {
  isRunning: boolean
  handle: number | null
  isAnimation: boolean
  cancel: () => void
  start: (callback: () => void) => void
}

const createAnimationObj = (): AnimationObj => {
  const obj: AnimationObj = {
    isRunning: true,
    handle: null,
    isAnimation: window.requestAnimationFrame != null,
    cancel: () => {
      obj.isRunning = false
      if (obj.isAnimation) {
        window.cancelAnimationFrame(obj.handle!)
      } else {
        window.clearTimeout(obj.handle!)
      }
    },
    start: (callback: () => void) => {
      if (obj.isRunning === false) {
        return
      }

      if (obj.isAnimation) {
        obj.handle = window.requestAnimationFrame(callback)
      } else {
        // IE Fallback
        obj.handle = window.setTimeout(callback, 1000 / 60) as any
      }
    },
  }

  return obj
}

export function checkAnimation(
  owner: Element,
  selector: string,
  visibleClsName: string,
): AnimationObj {
  const obj = createAnimationObj()

  const elementsToShow = owner.querySelectorAll(selector)

  function loop() {
    Array.prototype.forEach.call(elementsToShow, (element: Element) => {
      if (isElementInViewport(element)) {
        element.classList.add(visibleClsName)
      } else {
        element.classList.remove(visibleClsName)
      }
    })

    obj.start(loop)
  }

  // Call the loop for the first time
  loop()

  return obj
}

export function openURL(url: string, option?: string): () => void {
  return () => {
    // option - _blank - 원래 window와 같은 크기로 팝업 생성.
    // option - 'width=800,height=600' - 크기 지정.
    if (option == null) {
      window.open(url)
    } else {
      window.open(url, '', option)
    }
  }
}

export function removeTag(txt: string): string {
  const regex = /<(\/)?([a-zA-Z]*)(\\s[a-zA-Z]*=[^>]*)?(\\s)*(\/)?>/g
  const tagRemove = txt.replaceAll(regex, '')
  return tagRemove
}

interface ErrorWithCode {
  code?: number | string
  message?: string
  errors?: Array<{ extensions?: { code?: number | string }; message?: string }>
  graphQLErrors?: Array<{ extensions?: { code?: number | string }; message?: string }>
}

export function processError(
  error: ErrorWithCode,
  cbFunc: (code: number | string, message: string | undefined, error: any) => boolean | void,
): boolean {
  let ret = false
  if (error.code != null) {
    cbFunc(error.code, error.message, error)
    ret = true
  } else if (error.errors || error.graphQLErrors) {
    const errors = error.errors || error.graphQLErrors
    errors!.some((errItem) => {
      const code = errItem.extensions != null ? errItem.extensions.code : -1
      if (cbFunc(code!, errItem.message, errItem) === true) {
        return true
      }
      return false
    })
    ret = true
  }
  return ret
}

export async function processOpenedFile(
  file: File,
  onChange: ((file: File, idx: number, total: number) => Promise<boolean | void>) | null,
  onLoadDataURL: ((url: string, file: File, idx: number, total: number) => void) | null,
  acceptedType: string | null,
  sizeLimit: number | null,
  onSizeError: ((fileSize: number, sizeLimit: number, idx: number, total: number) => void) | null,
  idx: number,
  total: number,
): Promise<void> {
  // console.log(file);

  if (acceptedType != null) {
    const acceptList = acceptedType.split(',')
    const fileType = file.type.toLowerCase()
    acceptList.some((item) => {
      let ret = false
      const checkType = item.toLowerCase().trim()
      if (checkType === '*' || checkType === '*/*') {
        ret = true
      } else if (checkType.indexOf('.') === 0 && fileType.length > checkType.length) {
        // 파일 확장자.
        const ext = fileType.substring(fileType.length - checkType.length)
        ret = ext === checkType
      } else if (checkType.indexOf('/') > 0) {
        // mimeType
        const pos = checkType.indexOf('/*')
        if (pos > 0 && fileType.substring(0, pos) === checkType.substring(0, pos)) {
          // 해당되는 모든 타입 ex) image/* 등..
          ret = true
        } else if (fileType === checkType) {
          ret = true
        }
      }

      return ret
    })
  }

  if (sizeLimit == null || file.size < sizeLimit) {
    // this.uploadPhoto(file, loadingFunc)
    if (onChange != null) {
      const res = await onChange(file, idx, total)
      if (res === false) {
        return
      }
    }
    if (onLoadDataURL != null) {
      const reader = new FileReader()

      reader.onload = () => {
        const url = reader.result as string
        onLoadDataURL(url, file, idx, total)
      }

      reader.readAsDataURL(file)
    }
  } else if (onSizeError != null) {
    onSizeError(file.size, sizeLimit, idx, total)
  }
}

interface OpenFileObj {
  onChange: ((file: File, idx: number, total: number) => Promise<boolean | void>) | null
  onLoadDataURL: ((url: string, file: File, idx: number, total: number) => void) | null
  acceptedType: string | null
  sizeLimit: number | null
  onSizeError: ((fileSize: number, sizeLimit: number, idx: number, total: number) => void) | null
  checkSelectFiles?: (files: FileList) => boolean | Promise<boolean>
}

interface HTMLInputElementWithObj extends HTMLInputElement {
  openFileObj?: OpenFileObj
}

export function openFile(
  onChange: ((file: File, idx: number, total: number) => Promise<boolean | void>) | null,
  onLoadDataURL: ((url: string, file: File, idx: number, total: number) => void) | null,
  acceptedType: string | null,
  sizeLimit: number | null,
  onSizeError: ((fileSize: number, sizeLimit: number, idx: number, total: number) => void) | null,
  isMultiple?: boolean,
  checkSelectFiles?: (files: FileList) => boolean | Promise<boolean>,
): void {
  let input = document.querySelector(`#image-picker`) as HTMLInputElementWithObj | null
  if (input == null) {
    input = document.createElement('input') as HTMLInputElementWithObj
    input.id = 'image-picker'
    input.setAttribute('type', 'file')
    document.body.appendChild(input)

    input.addEventListener('change', async (evt) => {
      const target = evt.target as HTMLInputElementWithObj
      const { files } = target
      const checkFn = target.openFileObj?.checkSelectFiles
      if (files && checkFn != null) {
        const checkResult = checkFn(files)
        if (checkResult instanceof Promise) {
          if ((await checkResult) === false) {
            return
          }
        } else if (checkResult === false) {
          return
        }
      }
      if (files) {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i]
          await processOpenedFile(
            file,
            input!.openFileObj!.onChange,
            input!.openFileObj!.onLoadDataURL,
            null,
            input!.openFileObj!.sizeLimit,
            input!.openFileObj!.onSizeError,
            i,
            files.length,
          )
        }
      }
    })
  }
  const openFileObj: OpenFileObj = {
    onChange,
    onLoadDataURL,
    acceptedType,
    sizeLimit,
    onSizeError,
    checkSelectFiles,
  }

  input.openFileObj = openFileObj
  if (isMultiple === true) {
    input.multiple = true
  } else {
    input.multiple = false
  }
  if (acceptedType != null) {
    input.setAttribute('accept', openFileObj.acceptedType!)
  }

  input.value = ''
  ;(window as any).prevInput = input
  /*
    Note: In modern browsers input[type="file"] is functional without
    even adding it to the DOM, but that might not be the case in some older
    or quirky browsers like IE, so you might want to add it to the DOM
    just in case, and visually hide it. And do not forget do remove it
    once you do not need it anymore.
  */

  input.click()
}

export function getTextFromHTML(
  htmlStr: string | null | undefined,
  processNode?: (node: HTMLElement) => void,
): string {
  if (htmlStr == null) {
    return ''
  }
  // DOMParser 의 inactive document 는 img/iframe 등의 src 를 fetch 하지 않으므로
  // 본문에 `cid:` 또는 외부 추적 픽셀이 있어도 네트워크 요청이 발생하지 않는다.
  const cleaned = htmlStr.replace(/\r?\n/gm, '')
  const doc = new DOMParser().parseFromString(cleaned, 'text/html')
  if (processNode != null) {
    processNode(doc.body)
  }
  return normalizeSpaces(doc.body.textContent || '')
}

function normalizeSpaces(s: string): string {
  // NBSP(00A0), 좁은 NBSP(202F), figure space(2007), thin space(2009), 등
  return (
    s
      .replace(/[\u00A0\u202F\u2007\u2009]/g, ' ')
      // Zero-width류 제거:
      .replace(/[\u200B-\u200D\uFEFF\u2060]/g, '')
  )
}

export function textToEntityText(text: string): string {
  const EOL = /(\n|\r\n|\n\r)/g

  // const entityText = text;
  const emptyDiv = document.createElement('div')
  emptyDiv.className = 'temp-html-parser'
  emptyDiv.innerText = text
  document.body.appendChild(emptyDiv)
  let entityText = emptyDiv.innerHTML
  emptyDiv.remove()

  entityText = entityText.replace(EOL, '<br />')
  entityText = entityText.replace(/ /g, '&nbsp;')
  entityText = entityText.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
  return entityText
}

export function textToHTML(text: string): string {
  const entityText = textToEntityText(text)
  const bodyTextToHTML = `<p>${entityText}</p>`
  return bodyTextToHTML
}

export function getTextWidth(text: string | null | undefined, fontSize?: string): number {
  if (text == null) {
    return 0
  }
  const emptyDiv = document.createElement('div')
  emptyDiv.className = 'temp-html-parser'
  const textSpan = document.createElement('span')
  textSpan.style.wordBreak = 'keep-all'
  textSpan.style.whiteSpace = 'nowrap'
  textSpan.style.fontSize = fontSize || '12px'
  emptyDiv.appendChild(textSpan)

  textSpan.innerText = text

  document.body.appendChild(emptyDiv)

  const width = textSpan.offsetWidth
  emptyDiv.remove()

  return width
}

export function reactTextContent(elem: any): string {
  return innerText(elem)
}

export function copyElement(element: HTMLElement): void {
  navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': new Blob([element.innerText], { type: 'text/plain' }),
      'text/html': new Blob([element.innerHTML], { type: 'text/html' }),
    }),
  ])
}

export function resizeImg(
  imgObj: HTMLImageElement,
  w: number,
  h: number,
  type: string,
  onResized: (blob: Blob | null) => void,
): void {
  const canvas = document.createElement('canvas')
  const canvasContext = canvas.getContext('2d')

  canvas.width = w
  canvas.height = h

  canvasContext!.drawImage(imgObj, 0, 0, w, h)

  // const dataURI = canvas.toDataURL("image/jpeg");

  // SVG인 경우 PNG로 변환
  const outputType = type === 'image/svg+xml' ? 'image/png' : type

  canvas.toBlob(
    (blob) => {
      console.log('toBlob - ', blob, canvas)

      onResized(blob)
    },
    outputType,
    0.9,
  )
}

export async function convertHEIC(
  file: File,
  toType?: string,
  quality?: number,
): Promise<Blob | File> {
  const blob = new Blob([file], { type: file.type })
  return await blobToHEIC(blob, toType, quality)
}

export async function blobToHEIC(
  blob: Blob,
  toType?: string,
  quality?: number,
): Promise<Blob | File> {
  const options: any = { blob }
  if (toType != null && (await isHeic(blob as File))) {
    options.type = toType
    if (toType === 'image/jpeg' && quality != null) {
      options.quality = quality
    }
  }
  const result = await heicTo(options)
  return result as unknown as Blob | File
}

const unescapeDiv = document.createElement('div')

export function unescapeText(str: string): string {
  unescapeDiv.innerHTML = str
  return unescapeDiv.textContent || ''
}

export function compareFile(a: File | null, b: File | null): boolean {
  if (a != null && b != null) {
    return (
      a.name === b.name &&
      a.size === b.size &&
      a.lastModified === b.lastModified &&
      a.type === b.type
    )
  }
  return a === b
}

interface UniqueKeyValue {
  lastTime: number
  idx: number
}

const uniqueKeyMap: Record<string, UniqueKeyValue> = {}

export function deleteUniqueKeyName(name: string): void {
  delete uniqueKeyMap[name]
}

export function getUniqueKey(name: string): string {
  if (uniqueKeyMap[name] == null) {
    uniqueKeyMap[name] = {
      lastTime: -1,
      idx: 1,
    }
  }

  const valMgr = uniqueKeyMap[name]

  let keyStr: string
  const curTime = Date.now()
  if (valMgr.lastTime !== curTime) {
    keyStr = `${curTime}`
    valMgr.lastTime = curTime
    valMgr.idx = 1
  } else {
    keyStr = `${curTime}_${valMgr.idx}`
    valMgr.idx += 1
  }

  return keyStr
}

export function findScrollableParent(element: HTMLElement): HTMLElement | HTMLBodyElement {
  let parent = element.parentElement

  const canOverflow = (overflow: string): boolean => {
    return overflow !== 'visible' && overflow !== 'clip'
  }

  const isScrollable = (element: HTMLElement): boolean => {
    if (element.scrollHeight > element.clientHeight) {
      const style = window.getComputedStyle(element)
      return canOverflow(style.overflow) || canOverflow(style.overflowY)
    }
    return false
  }

  while (parent && parent !== document.body) {
    if (isScrollable(parent)) {
      return parent
    }

    parent = parent.parentElement
  }

  // 스크롤 가능한 부모가 없으면 document.body를 반환
  return document.body
}

interface ScrollOptions {
  behavior?: 'auto' | 'smooth'
}

export function scrollIntoViewIfNeeded(elem: HTMLElement | null, options?: ScrollOptions): void {
  if (elem == null) {
    return
  }

  // 요소가 뷰포트 내에 있는지 확인하는 함수
  function isElementInViewport(
    element: HTMLElement,
    scrollContainer: HTMLElement | Window,
  ): boolean {
    const OFFSET = 100
    const elementRect = element.getBoundingClientRect()

    const getBoundary = (): { top: number; bottom: number } => {
      if (scrollContainer === window) {
        return {
          top: OFFSET,
          bottom: window.innerHeight - OFFSET,
        }
      }

      const containerRect = (scrollContainer as HTMLElement).getBoundingClientRect()
      return {
        top: containerRect.top + OFFSET,
        bottom: containerRect.bottom - OFFSET,
      }
    }

    const boundary = getBoundary()
    return elementRect.top <= boundary.bottom && elementRect.bottom >= boundary.top
  }

  // 스크롤 가능한 부모 요소 찾기
  const scrollContainer = findScrollableParent(elem)

  // 요소가 이미 뷰포트 내에 있는지 확인
  if (isElementInViewport(elem, scrollContainer)) {
    return // 이미 보이므로 스크롤하지 않음
  }

  const elementRect = elem.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()

  scrollContainer.scrollTo({
    top: scrollContainer.scrollTop + elementRect.top - containerRect.top,
    behavior: options != null && options.behavior != null ? options.behavior : 'auto',
  })
}

export function getHiddenIFrame(): HTMLIFrameElement {
  let iframe = document.body.querySelector('#hidden-iframe') as HTMLIFrameElement | null
  if (iframe != null) {
    // 재사용 시 parsing/rendering 시간이 이유없이 증가하는 문제가 발생하여 재생성하도록 함.
    iframe.remove()
    iframe = null
  }
  if (iframe == null) {
    const hiddenIFrame = document.createElement('iframe')
    hiddenIFrame.id = 'hidden-iframe'
    // allow-same-origin 만 부여 — 부모에서 contentWindow.getComputedStyle 호출이 필요하기 때문.
    // allow-scripts 와 동시 부여 시 sandbox escape 가 가능 (iframe 안 script 가 부모 DOM 에서
    // 자기 자신의 sandbox 속성을 제거 후 reload 가능) 하여 의도적으로 추가하지 않는다.
    // 메일 본문 HTML 의 script 는 doc.write 전 sanitizeHtmlForIframeWrite 로 제거된다.
    // hiddenIFrame.sandbox.add('allow-same-origin')
    document.body.appendChild(hiddenIFrame)
    iframe = hiddenIFrame
  }

  return iframe
}

// iframe을 버리기 직전 호출 — contentDocument의 img에서 리소스 참조를 끊어
// detached 문서의 active 상태를 해제하고 GC를 돕는다. (src를 가진/로드 중인 img가 있는
// Document는 브라우저가 active로 보고 회수를 미루므로, iframe.remove()/about:blank 전에 분리)
// cross-origin이면 contentDocument 접근에서 throw → try/catch로 무시(그 경우 브라우저가 회수).
// clearDocument는 문서를 통째로 버리는 경우(temp iframe)만 true — 컴포넌트 소유 iframe은
// 직후 about:blank로 문서가 교체되므로 트리 비우기가 불필요하다.
export function detachIFrameImages(
  iframe: HTMLIFrameElement | null | undefined,
  opts?: { clearDocument?: boolean },
): void {
  if (iframe == null) return
  try {
    const cdoc = iframe.contentDocument
    if (cdoc != null) {
      cdoc.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('src')
        img.removeAttribute('srcset')
        img.remove()
      })
      if (opts?.clearDocument === true) {
        cdoc.documentElement?.replaceChildren()
      }
    }
  } catch (e) {
    console.warn('[detachIFrameImages] 정리 실패:', e)
  }
}

// getSafeHTML 호출별 독립 iframe 생성. 공유 #hidden-iframe을 사용하면 비동기 처리
// 도중 다음 호출이 iframe을 제거해 이전 호출의 DOMContentLoaded가 영원히 오지 않고,
// 미해결 promise가 detached iframe document(메일 DOM 전체)를 영구 보존하는 경쟁이 발생한다.
// sandbox 미부여 자세는 #hidden-iframe(getHiddenIFrame)과 동일 — 부모의 contentWindow
// getComputedStyle 접근이 필요하고, script는 doc.write 전 sanitizeHtmlForIframeWrite로 제거된다.
// 숨김 스타일은 inline으로 적용한다.
function createTempHiddenIFrame(): HTMLIFrameElement {
  const hiddenIFrame = document.createElement('iframe')
  hiddenIFrame.style.visibility = 'hidden'
  hiddenIFrame.style.position = 'absolute'
  hiddenIFrame.style.bottom = '0'
  hiddenIFrame.style.left = '0'
  hiddenIFrame.style.height = '1px'
  document.body.appendChild(hiddenIFrame)
  return hiddenIFrame
}

/**
 * `doc.write` 로 iframe 에 주입되기 전 메일 본문 HTML 을 무력화한다.
 *
 * - `<script>` 노드 제거 — `allow-scripts` 없이도 sandbox 가 "Blocked script execution" 콘솔
 *   경고를 띄우는 것을 차단.
 * - `<noscript>` 노드 제거 — DOMParser 는 script-enabled 컨텍스트라 noscript 내용을 raw
 *   text 로 보관했다가, script-disabled sandbox iframe 의 doc.write 에서 다시 HTML 로
 *   파싱되며 그 안의 `<script>` 가 부활하는 우회 경로를 막는다.
 * - 모든 요소의 `on*` 인라인 이벤트 핸들러 속성 제거.
 * - `href` / `src` 의 `javascript:` URL 제거.
 *
 * DOMParser 가 만든 inactive document 위에서 동작하므로 sanitize 자체로는 리소스 fetch 가
 * 발생하지 않는다.
 */
function sanitizeHtmlForIframeWrite(doc: Document): void {
  doc.querySelectorAll('script, noscript').forEach((el) => el.remove())
  doc.querySelectorAll('*').forEach((el) => {
    for (let i = el.attributes.length - 1; i >= 0; i--) {
      const attrName = el.attributes[i].name
      if (attrName.length > 2 && attrName.toLowerCase().startsWith('on')) {
        el.removeAttribute(attrName)
      }
    }
    // 신뢰 불가 이메일 입력이 위조한 의사요소 보존 마커 제거. getSafeHTML 의 <style> 일괄 제거는
    // 이 마커를 skip 하므로, 마커를 미리 지워 프로그램적으로 주입한 <style> 만 살아남도록 한다
    // (미제거 시 @scope 미지원 브라우저/스코프 미적용 순간에 임의 CSS 가 전역 적용될 수 있음).
    if (el.hasAttribute(PSEUDO_STYLE_MARKER)) {
      el.removeAttribute(PSEUDO_STYLE_MARKER)
    }
    const href = el.getAttribute('href')
    if (href != null && /^\s*javascript:/i.test(href)) {
      el.removeAttribute('href')
    }
    const src = el.getAttribute('src')
    if (src != null && /^\s*javascript:/i.test(src)) {
      el.removeAttribute('src')
    }
  })
}

interface CSSValue {
  type: number
  number?: number
  unit?: string
  realUnit?: string
  isRel?: boolean
  value: string
}

// css value를 parsing - 숫자 + 단위로 구분.
export function getCSSValue(str: string, isAttr?: boolean, remSize?: number): CSSValue {
  const matchList = str.match(/^(-{0,1}\d+[.]*\d*|\d*[.]*\d+)/)
  if (matchList != null && matchList.length > 0) {
    const numStr = matchList[0]
    let unit = str.substring(numStr.length)
    let num = 0
    if (numStr.indexOf('.') > -1) {
      num = parseFloat(numStr)
    } else {
      num = parseInt(numStr)
    }
    switch (unit) {
      case 'rem':
        const remNum = remSize != null ? remSize : 16
        num = num * remNum
        unit = 'px'
        break
      case 'pt':
        num = (num * 4) / 3
        unit = 'px'
        break
      case 'in':
        num = num * 96
        unit = 'px'
        break
      case 'pc':
        num = num * 16
        unit = 'px'
        break
      case 'cm':
        num = num * 37.79
        unit = 'px'
        break
      case 'mm':
        num = num * 3.779
        unit = 'px'
        break
    }
    if (isAttr == true && unit == '') {
      unit = 'px'
    }
    return {
      type: 1, // 숫자 + 단위.
      number: num,
      unit: unit.length == 0 ? 'px' : unit,
      realUnit: unit,
      isRel: unit != 'px', // 'px', 'pt' 이외의 단위는 상대적인 값으로 간주함.
      value: str,
    }
  }
  return {
    type: 0, // 기타 값.
    value: str,
  }
}

const cssImportToLink = (styles: StyleSheetList | CSSStyleSheet[], doc: Document): void => {
  Array.from(styles).forEach((style) => {
    const sheet = (style as any).sheet || style
    if (sheet.cssRules == null) {
      console.error(' --- invalid stylesheet : ', sheet)
    } else if (sheet.href == null || sheet.href.length == 0) {
      let changed = false
      let lastLink: HTMLLinkElement | null = null
      for (let i = sheet.cssRules.length - 1; i >= 0; i -= 1) {
        const rule = sheet.cssRules[i]
        const ruleType = rule.constructor.name
        if (ruleType === 'CSSImportRule') {
          // const importRule = rule;
          sheet.deleteRule(i)
          const extPos = (rule as any).href.lastIndexOf('.')
          let isFont = false
          if (extPos > -1) {
            const ext = (rule as any).href.substring(extPos + 1).toLowerCase()
            if (
              ext === 'woff2' ||
              ext === 'woff' ||
              ext === 'otf' ||
              ext === 'ttf' ||
              ext === 'eot'
            ) {
              isFont = true
            }
          }
          const link = doc.createElement('link')
          link.rel = 'preconnect'
          if (isFont === true) {
            link.as = 'font'
          }
          link.href = (rule as any).href
          if (doc.head.contains(sheet.ownerNode) === true) {
            doc.head.insertBefore(link, sheet.ownerNode)
          } else {
            if (lastLink == null) {
              doc.head.appendChild(link)
            } else {
              doc.head.insertBefore(link, lastLink)
            }
            lastLink = link
          }
          changed = true
        } else if (ruleType === 'CSSFontFaceRule') {
          // font-display 는 아래 cssText 재조립 단계에서 주입한다.
          // Firefox 는 CSSFontFaceRule.style 의 descriptor setter 를 거부함.
          changed = true
        }
      }
      if (changed === true) {
        let styleText = '\n/* modified by Officemail */\n'
        for (let i = 0; i < sheet.cssRules.length; i++) {
          const rule = sheet.cssRules[i]
          let cssText = rule.cssText
          if (rule.constructor.name === 'CSSFontFaceRule') {
            if (/font-display\s*:/i.test(cssText)) {
              cssText = cssText.replace(/font-display\s*:[^;}]*;?/i, 'font-display: swap;')
            } else {
              cssText = cssText.replace(/}\s*$/, '  font-display: swap;\n}')
            }
          }
          styleText += cssText + '\n'
        }
        sheet.ownerNode.innerHTML = styleText
      }
    } else {
      console.log(' --- external sheet : ', sheet.href)
    }
  })
}

const shorthandStyleMap: Record<string, string> = {
  'font-variant-alternates': 'font-variant',
  'font-variant-caps': 'font-variant',
  'font-variant-east-asian': 'font-variant',
  'font-variant-emoji': 'font-variant',
  'font-variant-ligatures': 'font-variant',
  'font-variant-numeric': 'font-variant',
  'font-variant-position': 'font-variant',
  'border-color': 'border',
  'border-style': 'border',
  'border-width': 'border',
  'border-top': 'border',
  'border-left': 'border',
  'border-right': 'border',
  'border-bottom': 'border',
  'border-top-color': 'border-top',
  'border-top-style': 'border-top',
  'border-top-width': 'border-top',
  'border-left-color': 'border-left',
  'border-left-style': 'border-left',
  'border-left-width': 'border-left',
  'border-right-color': 'border-right',
  'border-right-style': 'border-right',
  'border-right-width': 'border-right',
  'border-bottom-color': 'border-bottom',
  'border-bottom-style': 'border-bottom',
  'border-bottom-width': 'border-bottom',
  'padding-left': 'padding',
  'padding-right': 'padding',
  'padding-top': 'padding',
  'padding-bottom': 'padding',
  'padding-block-start': 'padding-block',
  'padding-block-end': 'padding-block',
  'padding-inline-start': 'padding-inline',
  'padding-inline-end': 'padding-inline',
  'margin-left': 'margin',
  'margin-right': 'margin',
  'margin-top': 'margin',
  'margin-bottom': 'margin',
  'margin-block-start': 'margin',
  'margin-block-end': 'margin',
  'margin-inline-start': 'margin',
  'margin-inline-end': 'margin',
  'text-decoration-line': 'text-decoration',
  'text-decoration-style': 'text-decoration',
  'text-decoration-color': 'text-decoration',
  'text-decoration-thickness': 'text-decoration',
}

// 주입 의사요소 <style> 식별 마커. 이 속성이 있는 <style> 은 sanitize 의 <style> 일괄 제거에서 제외.
const PSEUDO_STYLE_MARKER = 'data-ngw-pseudo'

interface GetSafeHTMLOptions {
  processElement?: (elem: HTMLElement) => void
  isAllowed?: (elem: HTMLElement) => boolean
  // 지정 시 툴바 인용구(variant) 의사요소 장식을 뷰어에 보존하는 <style> 을 결과에 주입한다(email id 기준 scope).
  pseudoScopeId?: string
}

// html 은 문자열 또는 이미 파싱된 Document 를 받는다. Document 를 넘기면 내부 DOMParser 재파싱을
// 생략한다(OFM-221: 동일 2MB HTML 중복 파싱 제거). Document 는 sanitize 로 mutate 되므로 호출부는
// 전달 후 해당 Document 를 재사용하지 않아야 한다.
export function getSafeHTML(
  html: string | Document,
  options?: GetSafeHTMLOptions | string,
  width?: string,
): Promise<string> {
  const promise = new Promise<string>((resolve) => {
    let opts: GetSafeHTMLOptions = {}
    if (typeof options === 'object') {
      opts = options
    }

    // OFM-221: Document 를 받으면 재파싱 생략(중복 파싱 제거). 문자열이면 기존대로 파싱한다.
    const docWithoutUI =
      typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html
    // active iframe 에 넣기 전 script/on*/javascript: 제거.
    sanitizeHtmlForIframeWrite(docWithoutUI)
    const styles = docWithoutUI.querySelectorAll('style')
    cssImportToLink(styles as any, docWithoutUI)
    const iframe = createTempHiddenIFrame()
    // 기본 폭 720px: 테스트 샘플에서 media query가 700px를 모바일 분기 기준으로 쓰는 케이스가
    // 있어, 기본값을 700px로 두면 일반(데스크톱) 레이아웃이 아닌 모바일 레이아웃으로 인라인된다.
    // 720px로 올려 해당 경계를 넘겨 일반 크기로 처리되도록 한다.
    iframe.style.width = width || '720px'

    const doc = iframe.contentDocument!
    // OFM-221: doc.write 재파싱 + outerHTML 재직렬화 대신, 파싱·sanitize 완료된 docWithoutUI 의
    // documentElement 를 iframe 문서로 adopt(이동) 후 root 교체 → 동일 HTML 의 재파싱을 제거한다.
    // sanitizeHtmlForIframeWrite 에서 script/noscript 를 이미 제거하므로 active context 로 옮겨도
    // 스크립트 실행 위험이 없다. <style> 은 연결 시 CSSOM 이 살아나 getRuleMap(styleSheets) 이 동작한다.
    const adoptedRoot = doc.adoptNode(docWithoutUI.documentElement)
    doc.replaceChild(adoptedRoot, doc.documentElement)

    let remSize = 16
    const rootFontSize = iframe.contentWindow!.getComputedStyle(doc.documentElement, null).fontSize
    const rootFSValue = getCSSValue(rootFontSize, false)
    if (rootFSValue.type == 1 && rootFSValue.isRel === false) {
      remSize = rootFSValue.number!
    }

    const processDoc = () => {
      const bodyElem = styleToInlineStyle(doc.body, {
        recursive: true,
        // properties: 'color background background-color font-weight border border-top border-left padding padding-left padding-top margin margin-left margin-right margin-top margin-bottom width height max-width max-height'.split(' '),
        win: iframe.contentWindow!,
        remSize, // root font size를 px 단위로 전달.
        clsPrefix: 'x_',
        idPrefix: 'x_',
        // 뷰어 경로(pseudoScopeId 전달 시)만 의사요소 <style> 주입. clsPrefix('x_') 와 동일 규칙으로 재작성.
        pseudoScopeId: opts.pseudoScopeId,
        pseudoStyleMarker: PSEUDO_STYLE_MARKER,
        processElement: (elem: Element) => {
          // 기존 이벤트 핸들러 제거
          ;(elem as any).onclick = ''
          ;(elem as any).onmousedown = ''
          ;(elem as any).onmouseup = ''
          ;(elem as any).onmousemove = ''
          ;(elem as any).onkeypress = ''
          ;(elem as any).onkeydown = ''
          ;(elem as any).onkeyup = ''
          ;(elem as any).onfocus = ''
          ;(elem as any).onblur = ''
          ;(elem as any).onselect = ''
          ;(elem as any).ondrag = ''
          ;(elem as any).ondrop = ''
          ;(elem as any).oncopy = ''
          ;(elem as any).oncut = ''
          ;(elem as any).onpaste = ''
          ;(elem as any).onchange = ''
          ;(elem as any).onerror = ''

          // 특히 위험한 누락 이벤트 핸들러 추가 제거
          ;(elem as any).onload = ''
          ;(elem as any).onunload = ''
          ;(elem as any).onbeforeunload = ''
          ;(elem as any).ondblclick = ''
          ;(elem as any).onmouseover = ''
          ;(elem as any).onmouseout = ''
          ;(elem as any).oncontextmenu = ''
          ;(elem as any).oninput = ''
          ;(elem as any).onsubmit = ''
          ;(elem as any).onreset = ''
          ;(elem as any).onabort = ''
          ;(elem as any).onmessage = ''

          // // 기타 이벤트 핸들러 (필요 시 활성화)
          // ;(elem as any).onmouseenter = ''
          // ;(elem as any).onmouseleave = ''
          // ;(elem as any).onwheel = ''
          // ;(elem as any).ontouchstart = ''
          // ;(elem as any).ontouchend = ''
          // ;(elem as any).ontouchmove = ''
          // ;(elem as any).ontouchcancel = ''
          // ;(elem as any).onfocusin = ''
          // ;(elem as any).onfocusout = ''
          // ;(elem as any).oninvalid = ''
          // ;(elem as any).onscroll = ''
          // ;(elem as any).onresize = ''
          // ;(elem as any).ontoggle = ''
          // ;(elem as any).onsearch = ''
          // ;(elem as any).onpointerdown = ''
          // ;(elem as any).onpointerup = ''
          // ;(elem as any).onpointermove = ''
          // ;(elem as any).onpointerover = ''
          // ;(elem as any).onpointerout = ''
          // ;(elem as any).onpointerenter = ''
          // ;(elem as any).onpointerleave = ''
          // ;(elem as any).onpointercancel = ''
          // ;(elem as any).ongotpointercapture = ''
          // ;(elem as any).onlostpointercapture = ''
          // ;(elem as any).onanimationstart = ''
          // ;(elem as any).onanimationend = ''
          // ;(elem as any).onanimationiteration = ''
          // ;(elem as any).ontransitionend = ''
          // ;(elem as any).ontransitionrun = ''
          // ;(elem as any).ontransitionstart = ''
          // ;(elem as any).ontransitioncancel = ''
          // ;(elem as any).onplay = ''
          // ;(elem as any).onpause = ''
          // ;(elem as any).onended = ''
          // ;(elem as any).onvolumechange = ''
          // ;(elem as any).onloadstart = ''
          // ;(elem as any).onprogress = ''
          // ;(elem as any).onseeked = ''
          // ;(elem as any).onseeking = ''
          // ;(elem as any).onstalled = ''
          // ;(elem as any).onsuspend = ''
          // ;(elem as any).ontimeupdate = ''
          // ;(elem as any).onwaiting = ''
          // ;(elem as any).oncanplay = ''
          // ;(elem as any).oncanplaythrough = ''
          // ;(elem as any).ondurationchange = ''
          // ;(elem as any).onemptied = ''
          // ;(elem as any).onloadeddata = ''
          // ;(elem as any).onloadedmetadata = ''
          // ;(elem as any).onratechange = ''

          // on* 속성 일괄 제거 (미래 이벤트 대응)
          const attributes = elem.attributes
          for (let i = attributes.length - 1; i >= 0; i--) {
            const attrName = attributes[i].name
            if (attrName.length > 2 && attrName.startsWith('on')) {
              elem.removeAttribute(attrName)
            }
          }

          elem.removeAttribute('contenteditable')

          if (opts.processElement != null) {
            opts.processElement(elem as HTMLElement)
          }
        },
        shorthandCheck: (element: Element, prop: string) => {
          if (shorthandStyleMap[prop] != null) {
            let shorthandProp = prop
            let shorthandValue: string | null = null

            let value: string
            const htmlElement = element as HTMLElement
            do {
              if (shorthandStyleMap[shorthandProp] != null) {
                value = htmlElement.style.getPropertyValue(shorthandStyleMap[shorthandProp])
                if (value != null && value.length > 0) {
                  shorthandProp = shorthandStyleMap[shorthandProp]
                  shorthandValue = value
                }
              } else {
                break
              }
            } while (value != null && value.length > 0)

            if (shorthandValue != null && shorthandValue.length > 0) {
              return {
                prop: shorthandProp,
                value: htmlElement.style.getPropertyValue(shorthandProp),
                isImportant: htmlElement.style.getPropertyPriority(shorthandProp) === 'important',
              }
            }
          }
          return null
        },
      })

      bodyElem.querySelectorAll('script').forEach((elem) => {
        elem.parentElement!.removeChild(elem)
      })

      bodyElem.querySelectorAll('style').forEach((elem) => {
        // 의사요소 보존용 주입 <style>(마커 부착)은 유지 — 인라인화 불가한 ::before/::after 장식을 담고 있다.
        if (elem.hasAttribute(PSEUDO_STYLE_MARKER)) return
        elem.parentElement!.removeChild(elem)
      })

      // const newBody = document.createElement('div')
      // newBody.innerHTML = doc.body.innerHTML

      // Array.from(doc.body.attributes).forEach((attr) => {
      //   newBody.setAttribute(attr.name, attr.value)
      // })

      // @todo - minify 또는 prettify 기능 추가 필요.

      // const newHtml = new XMLSerializer().serializeToString(newBody);
      // return newBody.outerHTML

      // 결과 문자열을 먼저 캡처(src 보존)한 뒤, 클론 트리의 img에서 리소스 참조를 끊는다.
      // styleToInlineStyle이 cloneNode로 만든 클론 img는 temp iframe 문서 소유라
      // src가 살아있으면 temp 문서를 active로 잡아 GC를 막는다. (네트워크 fetch가
      // 완료될 수는 있으나 active wrappable 참조 사슬이 끊겨 문서는 회수 가능해진다.)
      const resultHTML = bodyElem.outerHTML
      bodyElem.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('src')
        img.removeAttribute('srcset')
        img.remove()
      })
      resolve(resultHTML)
    }

    // 처리 직후 temp iframe을 제거 — 메일 콘텐츠를 담은 iframe이 body에 상주하지 않도록 한다.
    const finishDoc = () => {
      try {
        processDoc()
      } finally {
        // temp 문서를 통째로 버리므로 문서 트리의 img까지 분리(clearDocument:true).
        detachIFrameImages(iframe, { clearDocument: true })
        iframe.remove()
      }
    }

    if (doc.readyState === 'complete' || doc.readyState === 'interactive') {
      finishDoc()
    } else {
      const onLoaded = () => {
        doc.removeEventListener('DOMContentLoaded', onLoaded)
        finishDoc()
      }
      doc.addEventListener('DOMContentLoaded', onLoaded)
    }
  })

  return promise
}

function travelNode(node: HTMLElement, processFunc: (node: HTMLElement) => void): void {
  processFunc(node)
  if (node.children.length > 0) {
    Array.from(node.children).forEach((child) => {
      travelNode(child as HTMLElement, processFunc)
    })
  }
}

export function getViewText(htmlText: string): string {
  // 정규식 sanitize 는 `<scRIpt>`, `</script ...>`, 닫는 태그 누락 케이스 등 우회 벡터가
  // 통과하므로 DOMParser 로 파싱 후 sanitizeHtmlForIframeWrite 로 처리한다.
  // inactive document 위에서 정리 → 외부 리소스 fetch 없이 안전하게 직렬화.
  const sanitizedDoc = new DOMParser().parseFromString(htmlText, 'text/html')
  sanitizeHtmlForIframeWrite(sanitizedDoc)
  const safeHtml = sanitizedDoc.documentElement.outerHTML

  const iframe = getHiddenIFrame()
  const doc = iframe.contentDocument!
  doc.open()
  doc.write(safeHtml)
  doc.close()
  return doc.documentElement.innerText
}

export function getViewHtml(htmlText: string, processFunc?: (doc: Document) => void): string {
  const domParser = new DOMParser()

  const doc = domParser.parseFromString(htmlText, 'text/html')

  doc.body.querySelectorAll('script').forEach((elem) => {
    elem.parentElement!.removeChild(elem)
  })

  travelNode(doc.body, (elem) => {
    elem.removeAttribute('onclick')
    elem.removeAttribute('onmousedown')
    elem.removeAttribute('onmouseup')
    elem.removeAttribute('onmousemove')
    elem.removeAttribute('onkeypress')
    elem.removeAttribute('onkeydown')
    elem.removeAttribute('onkeyup')
    elem.removeAttribute('onfocus')
    elem.removeAttribute('onblur')
    elem.removeAttribute('onselect')
    elem.removeAttribute('ondrag')
    elem.removeAttribute('ondrop')
    elem.removeAttribute('oncopy')
    elem.removeAttribute('oncut')
    elem.removeAttribute('onpaste')
    elem.removeAttribute('onchange')
    elem.removeAttribute('onerror')
  })

  if (processFunc != null) {
    processFunc(doc)
  }

  const newHtml = new XMLSerializer().serializeToString(doc)
  return newHtml
}

export function toCamalCase(str: string): string {
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase())
}

export type FileType = {
  name?: string
  size?: number
  type?: string
}

export function equalFile(file1: FileType | null, file2: FileType | null): boolean {
  if (file1 == null || file2 == null) {
    return false
  }

  const type1 =
    file1.type != null && file1.type.length > 0 ? file1.type : 'application/octet-stream'
  const type2 =
    file2.type != null && file2.type.length > 0 ? file2.type : 'application/octet-stream'

  return file1.name === file2.name && file1.size === file2.size && type1 === type2
}

export function getCid(cidStr: string): string {
  if (cidStr.startsWith('<') === true && cidStr.endsWith('>') === true) {
    return cidStr.substring(1, cidStr.length - 1)
  }
  return cidStr
}

interface DateGroup {
  key: string
  label: string
  order?: number
  sortDate?: string
}

// 날짜 그룹화 유틸리티 함수들
export function getDateGroup(createTime: string | null | undefined): DateGroup {
  if (!createTime) {
    return { key: 'unknown', label: '기타' }
  }

  const now = moment()
  const itemDate = moment(createTime)

  if (!itemDate.isValid()) {
    return { key: 'unknown', label: '기타' }
  }

  if (itemDate.isSame(now, 'day')) {
    return { key: 'today', label: i18n.t('email.date.today'), order: 1 }
  } else if (itemDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return { key: 'yesterday', label: i18n.t('email.date.yesterday'), order: 2 }
  } else if (itemDate.isSame(now, 'week')) {
    return { key: 'thisWeek', label: i18n.t('email.date.thisWeek'), order: 3 }
  } else if (itemDate.isSame(now, 'month')) {
    return { key: 'thisMonth', label: i18n.t('email.date.thisMonth'), order: 4 }
  } else {
    const monthYear = itemDate.isSame(now, 'year')
      ? itemDate.format('MMM')
      : itemDate.format('MMM yyyy')
    return {
      key: `month-${itemDate.format('YYYY-MM')}`,
      label: monthYear,
      order: 5,
      sortDate: itemDate.format('YYYY-MM'),
    }
  }
}

interface ItemWithInfo {
  info?: {
    origin?: {
      create_time?: string
    }
  }
}

interface DateGroupWithItems extends DateGroup {
  items: ItemWithInfo[]
}

export function groupItemsByDate(itemList: ItemWithInfo[]): DateGroupWithItems[] {
  if (!itemList || itemList.length === 0) {
    return []
  }

  const groups: Record<string, DateGroupWithItems> = {}

  itemList.forEach((item) => {
    const createTime = item.info?.origin?.create_time
    const group = getDateGroup(createTime)

    if (!groups[group.key]) {
      groups[group.key] = {
        ...group,
        items: [],
      }
    }
    groups[group.key].items.push(item)
  })

  // 그룹을 정렬된 배열로 변환
  const sortedGroups = Object.values(groups).sort((a, b) => {
    // 기본 순서 (오늘, 어제, 이번주, 이번달)
    if (a.order !== b.order) {
      return a.order! - b.order!
    }

    // 월별 그룹은 최신순으로 정렬
    if (a.order === 5 && b.order === 5) {
      return b.sortDate!.localeCompare(a.sortDate!)
    }

    return 0
  })

  return sortedGroups
}

const fileNamePattern = /^(.* )[(](\d+)[)]$/
export function createNewFileName(filename: string): string {
  const pos = filename.lastIndexOf('.')
  const ext = filename.substring(pos)
  const name = filename.substring(0, pos)

  let newName = name
  const found = newName.match(fileNamePattern)

  if (found != null && found.length === 3) {
    const idx = parseInt(found[2], 10) + 1
    newName = `${found[1]}(${idx})${ext}`
  } else {
    newName = `${name.trimEnd()} (1)${ext}`
  }

  return newName
}

// 특수문자를 '_'로 변환
export function generateFilename(text: string): string {
  const invalidCharPattern = /([\\/:*?\"<>|])|(\.$)/g
  const name = text.replace(invalidCharPattern, '_')
  return name
}

export function FileNameMgr(this: any): void {
  const self = this

  self.fileNameMap = {}

  const fileNamePattern = /^(.* )[(](\d+)[)]$/

  const createNewFileName = (filename: string): string => {
    const pos = filename.lastIndexOf('.')
    const ext = filename.substring(pos)
    const name = filename.substring(0, pos)

    let newName = name
    const found = newName.match(fileNamePattern)

    if (found != null && found.length === 3) {
      const idx = parseInt(found[2], 10) + 1
      newName = `${found[1]}(${idx})${ext}`
    } else {
      newName = `${name.trimEnd()} (1)${ext}`
    }

    return newName
  }

  self.reset = (): void => {
    self.fileNameMap = {}
  }

  self.generateFilename = (fileName: string): string => {
    let name = generateFilename(fileName)
    // 중복된 이름이 있으면 새로운 이름을 생성
    let key = name.toLowerCase()
    while (self.fileNameMap[key] != null) {
      name = createNewFileName(name)
      key = name.toLowerCase()
    }
    self.fileNameMap[key] = name
    return name
  }
}

interface ConnectionInfo {
  isOnline: boolean
  isInitialized: boolean
  onOnline?: () => void
  onOffline?: () => void
}

const connectionInfo: ConnectionInfo = {
  isOnline: false,
  isInitialized: false,
}

const ONLINE_CHECK_TIMEOUT_MS = 5000

interface Deferred {
  resolve: (value: boolean) => void
  // reject: (reason: unknown) => void
}

let initPromise: Promise<boolean> | null = null
const pendingQueue: Deferred[] = []

export async function checkOnline(): Promise<boolean> {
  if (window.navigator.onLine === true) {
    return true
  }

  if (connectionInfo.isInitialized) {
    return connectionInfo.isOnline
  }

  if (initPromise != null) {
    return new Promise<boolean>((resolve) => {
      pendingQueue.push({ resolve })
    })
  }

  initPromise = (async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), ONLINE_CHECK_TIMEOUT_MS)

      await fetch('https://www.google.com', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
        signal: controller.signal,
      })

      clearTimeout(timeoutId)
      connectionInfo.isOnline = true
    } catch {
      connectionInfo.isOnline = false
    }

    connectionInfo.onOnline = () => {
      connectionInfo.isOnline = true
    }
    connectionInfo.onOffline = () => {
      connectionInfo.isOnline = false
    }
    window.addEventListener('online', connectionInfo.onOnline)
    window.addEventListener('offline', connectionInfo.onOffline)

    connectionInfo.isInitialized = true

    const result = connectionInfo.isOnline
    for (const deferred of pendingQueue) {
      deferred.resolve(result)
    }
    pendingQueue.length = 0
    initPromise = null

    return result
  })()

  return initPromise
}

const utils = {
  getISODateStr,
  timeFormat,
  emailAddress,
  compareStr,
  getSearchParams,
  makeSearchString,
  getTotalPageCnt,
  i18Key,
  getTimeStr,
  openURL,
  processOpenedFile,
  openFile,
  getTextFromHTML,
  textToHTML,
  getTextWidth,
  reactTextContent,
  copyElement,
  resizeImg,
  convertHEIC,
  blobToHEIC,
  unescapeText,
  compareFile,
  getUniqueKey,
  scrollIntoViewIfNeeded,
  getHiddenIFrame,
  detachIFrameImages,
  getSafeHTML,
  getViewText,
  getViewHtml,
  toCamalCase,
  equalFile,
  getCid,
  generateFilename,
  FileNameMgr,
  checkOnline,
}

export default utils
